import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { get } from "../../helpers/aws_api_helper";
import { getApiErrorMessage } from "../../helpers/aws_api_helper";
import * as url from "../../helpers/url_helper";
import { summaryTheme as t } from "./styles";
import SummaryAccountDetailContent from "./SummaryAccountDetailContent";
import SummaryKpiCards from "../Dashboard/SummaryCard/SummaryKpiCards";
import SummarySeverityChart from "../Dashboard/SummaryCard/SummarySeverityChart";
import SummaryComplianceChart from "../Dashboard/SummaryCard/SummaryComplianceChart";
import SummaryCostChart from "../Dashboard/SummaryCard/SummaryCostChart";
import SummaryServicesTable from "../Dashboard/SummaryCard/SummaryServicesTable";
import { useSummaryData } from "../Dashboard/SummaryCard/useSummaryData";

const ACCOUNTS_PAGE_SIZE = 100;

const getAccountLabel = (account) => {
  const num = account?.account_number ?? account?.accountNumber ?? account?.id ?? "";
  const name = account?.name ?? account?.account_name ?? "";
  if (name && name !== num) return `${name} (${num})`;
  return num || "—";
};

const SummaryPage = () => {
  const [accounts, setAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(true);
  const [accountsError, setAccountsError] = useState(null);
  const [selectedAccountNumber, setSelectedAccountNumber] = useState("");

  const { data: summaryData, loading: summaryLoading, error: summaryError } = useSummaryData();
  const overall = summaryData?.overall ?? null;
  const services = summaryData?.services ?? [];

  const fetchAccounts = useCallback(async () => {
    setAccountsLoading(true);
    setAccountsError(null);
    try {
      const response = await get(url.GET_ARN_VIEWS_ACCOUNTS, {
        params: { page: 1, page_size: ACCOUNTS_PAGE_SIZE },
      });
      const status = response?.status;
      if (status >= 200 && status < 300) {
        const items = response?.items ?? [];
        const list = Array.isArray(items) ? items : [];
        setAccounts(list);
        if (list.length > 0) {
          const first = list[0];
          const num = first?.account_number ?? first?.accountNumber ?? first?.id ?? "";
          setSelectedAccountNumber((prev) => (prev ? prev : num));
        }
      } else {
        setAccountsError(getApiErrorMessage(response) || "Failed to load accounts");
      }
    } catch (err) {
      const errData = err?.data ?? err?.response?.data;
      setAccountsError(getApiErrorMessage(errData) || err?.message || "Failed to load accounts");
    } finally {
      setAccountsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  return (
    <Box
      sx={{
        padding: { xs: "1rem", sm: "1.5rem", md: "2rem" },
        mb: "1.5rem",
        backgroundColor: t.colors.background,
        minHeight: "100vh",
        fontFamily: t.fontFamily,
      }}
    >
      {/* Page title */}
      <Box sx={{ mb: 2.5 }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            color: t.colors.text,
            fontSize: "1.25rem",
            letterSpacing: "-0.01em",
            mb: 0.25,
            lineHeight: 1.2,
            fontFamily: t.fontFamily,
          }}
        >
          ARNs Insight
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: t.colors.textMuted,
            fontSize: "0.8125rem",
            fontWeight: 400,
            lineHeight: 1.5,
            fontFamily: t.fontFamily,
          }}
        >
          Organization summary and account-level CSPM & FinOps view.
        </Typography>
      </Box>

      {/* Top half: KPI cards and charts */}
      <Box
        sx={{
          borderRadius: t.radius.lg,
          backgroundColor: t.colors.card,
          border: `1px solid ${t.colors.border}`,
          boxShadow: t.shadow,
          p: 2,
          mb: 3,
        }}
      >
        {summaryLoading && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 4 }}>
            <CircularProgress size={24} sx={{ color: t.colors.accent }} />
            <Typography sx={{ color: t.colors.textMuted, fontSize: "0.875rem", fontFamily: t.fontFamily }}>Loading summary…</Typography>
          </Box>
        )}
        {summaryError && (
          <Typography sx={{ color: t.colors.destructive, fontSize: "0.875rem", fontFamily: t.fontFamily }}>{summaryError}</Typography>
        )}
        {!summaryLoading && !summaryError && (
          <>
            <Box sx={{ mb: 2 }}>
              <SummaryKpiCards overall={overall} />
            </Box>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }, gap: 2, mb: 2 }}>
              <SummarySeverityChart securityFailedBySeverity={overall?.security_failed_checks_by_severity_total} />
              <SummaryComplianceChart overall={overall} services={services} />
              <SummaryCostChart overall={overall} services={services} />
            </Box>
            <SummaryServicesTable services={services} />
          </>
        )}
      </Box>

      {/* Bottom half: Account details (dropdown in section header) */}
      <Box
        sx={{
          borderRadius: t.radius.lg,
          backgroundColor: t.colors.card,
          border: `1px solid ${t.colors.border}`,
          boxShadow: t.shadow,
          p: 2,
        }}
      >
        <SummaryAccountDetailContent
          accountNumber={selectedAccountNumber}
          showBackLink={false}
          accounts={accounts}
          accountsLoading={accountsLoading}
          accountsError={accountsError}
          onAccountChange={setSelectedAccountNumber}
          getAccountLabel={getAccountLabel}
        />
      </Box>
    </Box>
  );
};

export default SummaryPage;
